import React, { useState, useContext, useCallback, useMemo } from 'react';
import {
  X, ChevronRight, ChevronLeft, Check, Phone, User, Baby, BookOpen,
  CreditCard, Calendar, Clock, MapPin, AlertCircle, Loader2, Users, DollarSign
} from 'lucide-react';
import NumberFormat from 'react-number-format';
import {
  LanguageContext, CenterContext, ClassesContext, CustomersContext, UserContext
} from '../../../../Context';
import { net } from '../../../../database';


const STEPS = ['customer', 'student', 'bills', 'payment', 'confirm'];

const LESSON_OPTIONS = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 24, label: '24' },
  { value: 32, label: '32' },
  { value: 40, label: '40' },
  { value: 80, label: '80' },
];

const COST_OPTIONS = [
  { value: 100000, label: '100k' },
  { value: 120000, label: '120k' },
  { value: 140000, label: '140k' },
  { value: 160000, label: '160k' },
  { value: 180000, label: '180k' },
  { value: 200000, label: '200k' },
  { value: 220000, label: '220k' },
];

const BOOK_OPTIONS = [
  { value: 100000, label: '100k' },
  { value: 150000, label: '150k' },
  { value: 200000, label: '200k' },
  { value: 250000, label: '250k' },
  { value: 300000, label: '300k' },
];

const SCHOLARSHIP_OPTIONS = [
  { value: 100000, label: '100k' },
  { value: 200000, label: '200k' },
  { value: 500000, label: '500k' },
];

const DISCOUNT_OPTIONS = [
  { value: 50000, label: '50k' },
  { value: 100000, label: '100k' },
  { value: 200000, label: '200k' },
  { value: 500000, label: '500k' },
];

const PAYMENT_DESTINATIONS = [
  'Tiền mặt',
  'Tài khoản công ty',
  'Tài khoản cá nhân thu hộ',
];

function formatVND(amount) {
  if (!amount && amount !== 0) return '';
  return new Intl.NumberFormat('vi-VN').format(amount);
}

export default function EnrolDialog({ lead, onClose, onConverted, convertLead }) {
  const { l, language } = useContext(LanguageContext);
  const { center } = useContext(CenterContext);
  const { classes } = useContext(ClassesContext);
  const { findCustomerByPhone } = useContext(CustomersContext);
  const { user } = useContext(UserContext);
  

  const t = useCallback((key, fallback) => {
    try { return l[key]?.[language] || fallback || key; } catch { return fallback || key; }
  }, [l, language]);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const existingCustomer = useMemo(() => {
    if (!lead?.phone) return null;
    return findCustomerByPhone(lead.phone) || null;
  }, [lead?.phone, findCustomerByPhone]);

  const placementClass = useMemo(() => {
    if (!lead?.placementResult) return null;
    return classes.find(c => c._id === lead.placementResult) || null;
  }, [lead?.placementResult, classes]);

  const [customerForm, setCustomerForm] = useState({
    name: lead?.name || '',
    phone: lead?.phone || '',
    zalo: lead?.zalo || '',
    email: lead?.email || '',
    source: lead?.source || '',
    title: 'Anh',
    paymentFrequency: 1,
    address: '',
    marketingSource: lead?.source || '',
  });

  const [studentsForm, setStudentsForm] = useState(() => {
    if (lead?.children?.length > 0) {
      return lead.children.map(child => ({
        name: child.name || '',
        dateOfBirth: '',
        classId: lead.placementResult || '',
        firstClassDate: new Date().toISOString().split('T')[0],
        strengthsAndWeaknesses: child.currentLevel || '',
        school: '',
      }));
    }
    return [{
      name: '',
      dateOfBirth: '',
      classId: lead?.placementResult || '',
      firstClassDate: new Date().toISOString().split('T')[0],
      strengthsAndWeaknesses: '',
      school: '',
    }];
  });

  const [billsForm, setBillsForm] = useState(() => {
    const classFee = 100000;
    return [{
      numberOfLessons: 40,
      costPerLesson: classFee,
      hasBooks: false,
      booksAmount: 100000,
      booksDescription: '',
      hasScholarship: false,
      scholarshipAmount: 100000,
      scholarshipDescription: '',
      hasDiscount: false,
      discountAmount: 50000,
      discountDescription: '',
    }];
  });

  const [paymentForm, setPaymentForm] = useState({
    payNow: true,
    amount: 0,
    type: 0,
    destination: PAYMENT_DESTINATIONS[0],
  });

  const [createdRecords, setCreatedRecords] = useState(null);

  const updateCustomer = useCallback((field, value) => {
    setCustomerForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateStudent = useCallback((index, field, value) => {
    setStudentsForm(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  }, []);

  const updateBill = useCallback((index, field, value) => {
    setBillsForm(prev => prev.map((b, i) => i === index ? { ...b, [field]: value } : b));
  }, []);

  const billTotals = useMemo(() => {
    return billsForm.map(bill => {
      const lessons = bill.numberOfLessons || 0;
      const cost = bill.costPerLesson || 0;
      let tuition = lessons * cost;
      let scholarship = bill.hasScholarship ? (bill.scholarshipAmount || 0) : 0;
      let discount = bill.hasDiscount ? (bill.discountAmount || 0) : 0;
      let books = bill.hasBooks ? (bill.booksAmount || 0) : 0;
      return { tuition, scholarship, discount, books, total: tuition + books - scholarship - discount };
    });
  }, [billsForm]);

  const grandTotal = useMemo(() => {
    return billTotals.reduce((sum, b) => sum + b.total, 0);
  }, [billTotals]);

  const canProceed = useMemo(() => {
    switch (STEPS[step]) {
      case 'customer':
        return !!customerForm.name.trim() && !!customerForm.phone.trim();
      case 'student':
        return studentsForm.every(s => s.name.trim() && s.dateOfBirth);
      case 'bills':
        return billsForm.every(b => b.numberOfLessons > 0 && b.costPerLesson > 0);
      case 'payment':
        return true;
      case 'confirm':
        return true;
      default:
        return false;
    }
  }, [step, customerForm, studentsForm, billsForm]);

  const stepLabels = [
    t('enrol.customer', 'Customer'),
    t('enrol.student', 'Student'),
    t('enrol.bills', 'Bills'),
    t('enrol.payment', 'Payment'),
    t('enrol.confirm', 'Confirm'),
  ];

  if (!lead) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    let customerId = existingCustomer?._id || null;
    let createdStudents = [];

    try {
      if (!customerId) {
        const customerData = {
          name: customerForm.name,
          phone: customerForm.phone,
          phone2: customerForm.zalo,
          email: customerForm.email,
          marketingSource: customerForm.source,
          title: customerForm.title,
          paymentFrequency: customerForm.paymentFrequency,
          address: customerForm.address,
        };
        const custRes = await net.post('/customer', customerData);
        if (!custRes?._id) throw new Error(t('enrol.customerFailed', 'Failed to create customer'));
        customerId = custRes._id;
      }

      for (let i = 0; i < studentsForm.length; i++) {
        const s = studentsForm[i];
        const studentData = {
          name: s.name,
          dateOfBirth: s.dateOfBirth,
          customer: customerId,
          status: 'New',
          strengthsAndWeaknesses: s.strengthsAndWeaknesses,
          school: s.school,
        };
        const studRes = await net.post('/student', studentData);
        if (!studRes?._id) throw new Error(t('enrol.studentFailed', 'Failed to create student'));
        const studentId = studRes._id;
        let studentRecord = studRes;

        if (s.classId) {
          try {
            const classRes = await net.post(`/student/${studentId}/classes/add`, {
              _id: s.classId,
              startDate: s.firstClassDate,
            });
            if (classRes) studentRecord = { ...studentRecord, classes: classRes };
          } catch (err) {
            console.error('Failed to add student to class:', err);
          }
        }

        const bill = billsForm[i];
        if (bill && bill.numberOfLessons > 0 && bill.costPerLesson > 0) {
          const htClass = classes.find(c => c._id === s.classId);
          let costPerLessonReal = bill.costPerLesson;
          if (bill.hasScholarship) {
            costPerLessonReal -= Math.round((bill.scholarshipAmount || 0) / bill.numberOfLessons);
          }
          if (bill.hasDiscount) {
            costPerLessonReal -= Math.round((bill.discountAmount || 0) / bill.numberOfLessons);
          }

          const billsBody = [{
            billType: 'Tuition',
            amount: bill.numberOfLessons * bill.costPerLesson,
            description: `${bill.numberOfLessons} buổi lớp ${htClass?.name || ''}, Từ ngày: ${s.firstClassDate}`,
            classId: s.classId,
            loca: htClass?.managedBy || '',
            location: [htClass?.managedBy || ''],
            firstClassDate: s.firstClassDate,
            numberOfLessons: bill.numberOfLessons,
            costPerLesson: bill.costPerLesson,
            costPerLessonReal,
          }];

          if (bill.hasBooks && bill.booksAmount > 0) {
            billsBody.push({
              billType: 'Books',
              amount: bill.booksAmount,
              description: bill.booksDescription || 'Books',
              classId: s.classId,
              loca: htClass?.managedBy || '',
              location: [htClass?.managedBy || ''],
            });
          }
          if (bill.hasScholarship && bill.scholarshipAmount > 0) {
            billsBody.push({
              billType: 'Scholarship',
              amount: 0 - bill.scholarshipAmount,
              description: bill.scholarshipDescription || 'Scholarship',
              classId: s.classId,
              loca: htClass?.managedBy || '',
              location: [htClass?.managedBy || ''],
            });
          }
          if (bill.hasDiscount && bill.discountAmount > 0) {
            billsBody.push({
              billType: 'Discount',
              amount: 0 - bill.discountAmount,
              description: bill.discountDescription || 'Discount',
              classId: s.classId,
              loca: htClass?.managedBy || '',
              location: [htClass?.managedBy || ''],
            });
          }

          try {
            await net.post(`/student/${studentId}/bills/add`, billsBody);
          } catch (err) {
            console.error('Failed to create bills:', err);
          }
        }

        createdStudents.push(studentRecord);
      }

      if (paymentForm.payNow && grandTotal > 0) {
        try {
          const allBillIds = [];
          for (const student of createdStudents) {
            if (student.unpaidBills) {
              student.unpaidBills.forEach(b => allBillIds.push(b._id));
            }
          }
          if (allBillIds.length > 0) {
            const paymentData = {
              customerId,
              customerName: customerForm.name,
              customerCredit: existingCustomer?.credit || 0,
              students: createdStudents.map((s, i) => ({
                _id: s._id,
                name: s.name,
                unpaidBills: s.unpaidBills || [],
              })),
              amount: paymentForm.amount || grandTotal,
              type: paymentForm.type,
              destination: paymentForm.destination,
              excludedStudents: [],
              excludedBills: [],
            };
            await net.post(`/customers/${customerId}/payments/add`, paymentData);
          }
        } catch (err) {
          console.error('Failed to process payment:', err);
        }
      }

      if (onConverted) {
        onConverted(lead.id || lead._id, {
          converted: true,
          convertedAt: new Date().toISOString(),
          convertedCustomerId: customerId,
          convertedStudentIds: createdStudents.map(s => s._id),
        });
      }

      if (convertLead) {
        await convertLead(lead.id || lead._id, {
          converted: true,
          convertedAt: new Date().toISOString(),
          convertedCustomerId: customerId,
          convertedStudentIds: createdStudents.map(s => s._id),
        });
      }

      setCreatedRecords({ customerId, students: createdStudents });
    } catch (err) {
      setError(err.message || t('enrol.error', 'An error occurred'));
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    setStep(4);
  };

  const inputClass = `w-full px-3 py-2 rounded-[10px] border border-slate-200 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800/20 transition-colors duration-150`;

  const renderStepIndicator = () => (
    <div className="flex items-center gap-1 px-6 py-3 border-b border-slate-100 bg-slate-50/50">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          {i > 0 && <ChevronRight size={12} className="text-slate-300 mx-0.5" />}
          <button
            onClick={() => { if (i < step) setStep(i); }}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors duration-150 ${
              i === step
                ? 'bg-teal-800 text-white'
                : i < step
                  ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200'
                  : 'bg-slate-100 text-slate-400'
            }`}
          >
            {i < step ? <Check size={10} /> : <span>{i + 1}</span>}
            {stepLabels[i]}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  const renderCustomer = () => (
    <div className="space-y-4">
      {existingCustomer && (
        <div className="p-3 rounded-[10px] bg-emerald-50 border border-emerald-200">
          <div className="flex items-center gap-2 mb-1">
            <Check size={14} className="text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">
              {t('enrol.existingCustomer', 'Existing customer found')}
            </span>
          </div>
          <p className="text-sm text-emerald-700 ml-6">
            {existingCustomer.name} — {existingCustomer.phone}
            {existingCustomer.students?.length > 0 && (
              <span className="text-emerald-600 ml-2">
                ({existingCustomer.students.length} {t('enrol.existingStudents', 'students')})
              </span>
            )}
          </p>
          <p className="text-xs text-emerald-600 ml-6 mt-1">
            {t('enrol.willAddStudent', 'A new student will be added to this customer.')}
          </p>
        </div>
      )}

      {!existingCustomer && (
        <>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              {t('enrol.customerName', 'Customer Name')} *
            </label>
            <input
              type="text"
              value={customerForm.name}
              onChange={(e) => updateCustomer('name', e.target.value)}
              className={inputClass}
              placeholder={t('enrol.customerNamePlaceholder', 'Parent/guardian name')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('enrol.phone', 'Phone')} *
              </label>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <NumberFormat
                  format="###-###-####"
                  mask="_"
                  value={customerForm.phone}
                  className={`${inputClass} pl-9`}
                  readOnly
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('enrol.zalo', 'Zalo')}
              </label>
              <input
                type="text"
                value={customerForm.zalo}
                onChange={(e) => updateCustomer('zalo', e.target.value)}
                className={inputClass}
                placeholder={t('enrol.zaloNumber', 'Zalo number')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('enrol.paymentFrequency', 'Payment Frequency')}
              </label>
              <div className="flex gap-2">
                {[1, 3, 6].map(freq => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => updateCustomer('paymentFrequency', freq)}
                    className={`flex-1 px-3 py-2 rounded-[10px] text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                      customerForm.paymentFrequency === freq
                        ? 'bg-teal-50 border-teal-800 text-teal-800'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {freq} {t('enrol.months', freq === 1 ? 'month' : 'months')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('enrol.title', 'Title')}
              </label>
              <div className="flex gap-2">
                {['Anh', 'Chi'].map(title => (
                  <button
                    key={title}
                    type="button"
                    onClick={() => updateCustomer('title', title)}
                    className={`flex-1 px-3 py-2 rounded-[10px] text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                      customerForm.title === title
                        ? 'bg-teal-50 border-teal-800 text-teal-800'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              {t('enrol.address', 'Address')}
            </label>
            <input
              type="text"
              value={customerForm.address}
              onChange={(e) => updateCustomer('address', e.target.value)}
              className={inputClass}
              placeholder={t('enrol.addressPlaceholder', 'Home address (optional)')}
            />
          </div>
        </>
      )}
    </div>
  );

  const renderStudent = () => (
    <div className="space-y-4">
      {studentsForm.map((student, i) => {
        const htClass = classes.find(c => c._id === student.classId);
        const locName = (() => {
          if (!htClass?.location) return '';
          const loc = center?.locations?.find(l => l._id === htClass.location);
          return loc?.name || '';
        })();

        return (
          <div key={i} className="p-4 rounded-[10px] border border-slate-200 bg-white space-y-3">
            <div className="flex items-center gap-2">
              <Baby size={16} className="text-teal-800" />
              <span className="text-sm font-bold text-slate-800">
                {t('enrol.studentNumber', 'Student')} {studentsForm.length > 1 ? i + 1 : ''}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('enrol.studentName', 'Name')} *
                </label>
                <input
                  type="text"
                  value={student.name}
                  onChange={(e) => updateStudent(i, 'name', e.target.value)}
                  className={inputClass}
                  placeholder={t('enrol.studentNamePlaceholder', 'Student name')}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('enrol.dateOfBirth', 'Date of Birth')} *
                </label>
                <input
                  type="date"
                  value={student.dateOfBirth}
                  onChange={(e) => updateStudent(i, 'dateOfBirth', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('enrol.class', 'Class')}
                </label>
                <div className="px-3 py-2 rounded-[10px] border border-slate-200 bg-slate-50 text-sm text-slate-700">
                  {htClass ? (
                    <div className="flex items-center gap-2">
                      <BookOpen size={13} className="text-teal-800" />
                      <span className="font-semibold">{htClass.name}</span>
                      {locName && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin size={9} />{locName}
                        </span>
                      )}
                    </div>
                  ) : student.classId ? (
                    <span className="text-slate-400 italic">{t('enrol.classNotFound', 'Class not found')}</span>
                  ) : (
                    <span className="text-slate-400 italic">{t('enrol.noClassSelected', 'No class selected')}</span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('enrol.firstClassDate', 'First Class Date')}
                </label>
                <input
                  type="date"
                  value={student.firstClassDate}
                  onChange={(e) => updateStudent(i, 'firstClassDate', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('enrol.notes', 'Notes')}
              </label>
              <input
                type="text"
                value={student.strengthsAndWeaknesses}
                onChange={(e) => updateStudent(i, 'strengthsAndWeaknesses', e.target.value)}
                className={inputClass}
                placeholder={t('enrol.levelOrNotes', 'Level or notes')}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderBills = () => (
    <div className="space-y-4">
      {studentsForm.map((student, i) => {
        const bill = billsForm[i];
        if (!bill) return null;
        const htClass = classes.find(c => c._id === student.classId);
        const totals = billTotals[i];

        return (
          <div key={i} className="p-4 rounded-[10px] border border-slate-200 bg-white space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign size={16} className="text-emerald-600" />
                <span className="text-sm font-bold text-slate-800">
                  {t('enrol.billsFor', 'Bills for')} {student.name || `${t('enrol.student', 'Student')} ${i + 1}`}
                </span>
              </div>
              <span className="text-sm font-bold text-emerald-700">{formatVND(totals.total)}đ</span>
            </div>

            {htClass && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <BookOpen size={12} />
                <span>{htClass.name}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('enrol.lessons', 'Lessons')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {LESSON_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => updateBill(i, 'numberOfLessons', opt.value)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                        bill.numberOfLessons === opt.value
? 'bg-teal-50 border-teal-800 text-teal-800'
                           : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                       }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('enrol.costPerLesson', 'Cost / Lesson')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {COST_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => updateBill(i, 'costPerLesson', opt.value)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                        bill.costPerLesson === opt.value
                          ? 'bg-teal-50 border-teal-800 text-teal-800'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {bill.numberOfLessons > 0 && bill.costPerLesson > 0 && (
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">{t('enrol.tuition', 'Tuition')} ({bill.numberOfLessons} × {formatVND(bill.costPerLesson)}đ)</span>
                  <span className="font-semibold text-slate-700">{formatVND(totals.tuition)}đ</span>
                </div>
              </div>
            )}

            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">{t('enrol.books', 'Books')}</span>
                <button
                  type="button"
                  onClick={() => updateBill(i, 'hasBooks', !bill.hasBooks)}
                  className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-all duration-150 cursor-pointer ${
                    bill.hasBooks
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : 'bg-white border-slate-200 text-slate-400'
                  }`}
                >
                  {bill.hasBooks ? t('enrol.on', 'On') : t('enrol.off', 'Off')}
                </button>
              </div>
              {bill.hasBooks && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-wrap gap-1.5">
                    {BOOK_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updateBill(i, 'booksAmount', opt.value)}
                        className={`px-2 py-1 rounded text-[10px] font-semibold border transition-all duration-150 cursor-pointer ${
                          bill.booksAmount === opt.value
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={bill.booksDescription}
                    onChange={(e) => updateBill(i, 'booksDescription', e.target.value)}
                    className="px-2 py-1 rounded-lg border border-slate-200 text-xs text-slate-700"
                    placeholder={t('enrol.booksDescription', 'Description')}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">{t('enrol.scholarship', 'Scholarship')}</span>
                <button
                  type="button"
                  onClick={() => updateBill(i, 'hasScholarship', !bill.hasScholarship)}
                  className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-all duration-150 cursor-pointer ${
                    bill.hasScholarship
                      ? 'bg-amber-50 border-amber-300 text-amber-700'
                      : 'bg-white border-slate-200 text-slate-400'
                  }`}
                >
                  {bill.hasScholarship ? t('enrol.on', 'On') : t('enrol.off', 'Off')}
                </button>
              </div>
              {bill.hasScholarship && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-wrap gap-1.5">
                    {SCHOLARSHIP_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updateBill(i, 'scholarshipAmount', opt.value)}
                        className={`px-2 py-1 rounded text-[10px] font-semibold border transition-all duration-150 cursor-pointer ${
                          bill.scholarshipAmount === opt.value
                            ? 'bg-amber-50 border-amber-300 text-amber-700'
                            : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
                        }`}
                      >
                        -{opt.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={bill.scholarshipDescription}
                    onChange={(e) => updateBill(i, 'scholarshipDescription', e.target.value)}
                    className="px-2 py-1 rounded-lg border border-slate-200 text-xs text-slate-700"
                    placeholder={t('enrol.scholarshipDescription', 'Reason')}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">{t('enrol.discount', 'Discount')}</span>
                <button
                  type="button"
                  onClick={() => updateBill(i, 'hasDiscount', !bill.hasDiscount)}
                  className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-all duration-150 cursor-pointer ${
                    bill.hasDiscount
                      ? 'bg-violet-50 border-violet-300 text-violet-700'
                      : 'bg-white border-slate-200 text-slate-400'
                  }`}
                >
                  {bill.hasDiscount ? t('enrol.on', 'On') : t('enrol.off', 'Off')}
                </button>
              </div>
              {bill.hasDiscount && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-wrap gap-1.5">
                    {DISCOUNT_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updateBill(i, 'discountAmount', opt.value)}
                        className={`px-2 py-1 rounded text-[10px] font-semibold border transition-all duration-150 cursor-pointer ${
                          bill.discountAmount === opt.value
                            ? 'bg-violet-50 border-violet-300 text-violet-700'
                            : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
                        }`}
                      >
                        -{opt.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={bill.discountDescription}
                    onChange={(e) => updateBill(i, 'discountDescription', e.target.value)}
                    className="px-2 py-1 rounded-lg border border-slate-200 text-xs text-slate-700"
                    placeholder={t('enrol.discountDescription', 'Reason')}
                  />
                </div>
              )}
            </div>

            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex justify-between items-center">
              <span className="text-xs font-semibold text-emerald-700">{t('enrol.netTotal', 'Net Total')}</span>
              <span className="text-sm font-bold text-emerald-800">{formatVND(totals.total)}đ</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderPayment = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 rounded-[10px] border border-slate-200 bg-white">
        <div>
          <p className="text-sm font-semibold text-slate-800">{t('enrol.totalOwed', 'Total Owed')}</p>
          <p className="text-xs text-slate-500">{t('enrol.acrossStudents', 'Across all students')}</p>
        </div>
        <span className="text-lg font-bold text-slate-800">{formatVND(grandTotal)}đ</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setPaymentForm(prev => ({ ...prev, payNow: true }))}
          className={`p-4 rounded-[10px] border-2 text-left transition-all duration-150 cursor-pointer ${
            paymentForm.payNow
              ? 'border-teal-800 bg-teal-50'
              : 'border-slate-200 bg-white hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <CreditCard size={16} style={{ color: paymentForm.payNow ? '#0f766e' : '#94a3b8' }} />
            <span className="text-sm font-bold" style={{ color: paymentForm.payNow ? '#0f766e' : '#64748b' }}>
              {t('enrol.payNow', 'Pay Now')}
            </span>
          </div>
          <p className="text-xs text-slate-500">{t('enrol.collectPaymentNow', 'Collect payment from customer')}</p>
        </button>
        <button
          type="button"
          onClick={() => setPaymentForm(prev => ({ ...prev, payNow: false }))}
          className={`p-4 rounded-[10px] border-2 text-left transition-all duration-150 cursor-pointer ${
            !paymentForm.payNow
              ? 'border-slate-500 bg-slate-50'
              : 'border-slate-200 bg-white hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className={paymentForm.payNow ? 'text-slate-400' : 'text-slate-600'} />
            <span className={`text-sm font-bold ${paymentForm.payNow ? 'text-slate-500' : 'text-slate-700'}`}>
              {t('enrol.payLater', 'Pay Later')}
            </span>
          </div>
          <p className="text-xs text-slate-500">{t('enrol.createBillsOnly', 'Create bills, collect later')}</p>
        </button>
      </div>

      {paymentForm.payNow && (
        <div className="space-y-3 p-4 rounded-[10px] border border-slate-200 bg-white">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              {t('enrol.paymentAmount', 'Payment Amount')}
            </label>
            <input
              type="number"
              value={paymentForm.amount || grandTotal}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: parseInt(e.target.value) || 0 }))}
              className={inputClass}
              placeholder={formatVND(grandTotal)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              {t('enrol.paymentType', 'Payment Type')}
            </label>
            <div className="flex gap-2">
              {(center?.paymentTypes || ['Cash', 'Transfer']).map((pt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setPaymentForm(prev => ({ ...prev, type: idx }))}
                  className={`px-3 py-2 rounded-[10px] text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                    paymentForm.type === idx
                      ? 'bg-teal-50 border-teal-800 text-teal-800'
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {pt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              {t('enrol.destination', 'Destination')}
            </label>
            <div className="flex gap-2 flex-wrap">
              {PAYMENT_DESTINATIONS.map(dest => (
                <button
                  key={dest}
                  type="button"
                  onClick={() => setPaymentForm(prev => ({ ...prev, destination: dest }))}
                  className={`px-3 py-2 rounded-[10px] text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                    paymentForm.destination === dest
                      ? 'bg-teal-50 border-teal-800 text-teal-800'
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {dest}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderConfirm = () => (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-[10px] bg-red-50 border border-red-200 flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {submitting && (
        <div className="flex flex-col items-center gap-3 py-12">
          <Loader2 size={24} className="animate-spin text-teal-800" />
          <p className="text-sm text-slate-500">{t('enrol.creating', 'Creating records...')}</p>
        </div>
      )}

      {!submitting && !createdRecords && (
        <div className="space-y-3">
          <div className="p-4 rounded-[10px] border border-slate-200 bg-white">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              {t('enrol.customerSummary', 'Customer')}
            </h4>
            <div className="flex items-center gap-2">
              <User size={16} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-800">
                {existingCustomer ? existingCustomer.name : customerForm.name}
              </span>
              <span className="text-xs text-slate-500">
                <NumberFormat displayType="text" format="###-###-####" value={customerForm.phone} />
              </span>
              {existingCustomer && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  {t('enrol.existing', 'Existing')}
                </span>
              )}
            </div>
          </div>

          {studentsForm.map((student, i) => {
            const htClass = classes.find(c => c._id === student.classId);
            return (
              <div key={i} className="p-4 rounded-[10px] border border-slate-200 bg-white">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  {t('enrol.student', 'Student')} {studentsForm.length > 1 ? i + 1 : ''}
                </h4>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Baby size={14} className="text-slate-500" />
                    <span className="text-sm font-semibold text-slate-800">{student.name}</span>
                    {htClass && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <BookOpen size={10} />{htClass.name}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-bold text-emerald-700">{formatVND(billTotals[i]?.total)}đ</span>
                </div>
              </div>
            );
          })}

          <div className="p-4 rounded-[10px] border-2 border-emerald-200 bg-emerald-50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-emerald-800">
                {paymentForm.payNow ? t('enrol.totalToPay', 'Total to Pay') : t('enrol.totalBills', 'Total Bills')}
              </span>
              <span className="text-lg font-bold text-emerald-800">{formatVND(grandTotal)}đ</span>
            </div>
            {paymentForm.payNow && (
              <div className="flex items-center gap-3 mt-1 text-xs text-emerald-600">
                <span>{paymentForm.destination}</span>
                <span>•</span>
                <span>{center?.paymentTypes?.[paymentForm.type] || 'Cash'}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {!submitting && createdRecords && (
        <div className="flex flex-col items-center gap-3 py-8">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
            <Check size={32} className="text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">{t('enrol.success', 'Enrolment Complete')}</h3>
          <p className="text-sm text-slate-500 text-center">
            {t('enrol.successMessage', 'Customer, student(s), and bills have been created.')}
          </p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => {
                window.location.hash = `/customer/${createdRecords.customerId}`;
                onClose();
              }}
              className="px-4 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 transition-colors duration-150 cursor-pointer"
            >
              {t('enrol.viewCustomer', 'View Customer')}
            </button>
            {createdRecords.students?.[0]?._id && (
              <button
                onClick={() => {
                  window.location.hash = `/student/${createdRecords.students[0]._id}`;
                  onClose();
                }}
                className="px-4 py-2 rounded-[10px] text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 cursor-pointer"
              >
                {t('enrol.viewStudent', 'View Student')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (STEPS[step]) {
      case 'customer': return renderCustomer();
      case 'student': return renderStudent();
      case 'bills': return renderBills();
      case 'payment': return renderPayment();
      case 'confirm': return renderConfirm();
      default: return null;
    }
  };

  const isLastStep = step === STEPS.length - 1;
  const isDone = !!createdRecords;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('enrol.title', 'Enrol Student')}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              <NumberFormat displayType="text" format="###-###-####" value={lead.phone} />
              {lead.name && ` — ${lead.name}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {renderStepIndicator()}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {renderStepContent()}
        </div>

        {/* Footer */}
        {!isDone && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
            <button
              onClick={() => step > 0 && setStep(step - 1)}
              disabled={step === 0}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-[10px] text-sm font-semibold transition-colors duration-150 ${
                step === 0
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-600 hover:bg-slate-100 cursor-pointer'
              }`}
            >
              <ChevronLeft size={16} />
              {t('enrol.back', 'Back')}
            </button>

            {isLastStep ? (
              <button
                onClick={handleConfirm}
                disabled={submitting}
                className={`flex items-center gap-2 px-5 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 transition-colors duration-150 cursor-pointer ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                {t('enrol.confirmAndCreate', 'Confirm & Create')}
              </button>
            ) : (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed}
                className={`flex items-center gap-1.5 px-5 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 transition-colors duration-150 ${
                  canProceed ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                }`}
                
              >
                {t('enrol.next', 'Next')}
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        )}

        {isDone && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-[10px] text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
            >
              {t('enrol.close', 'Close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
